import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { v4 as uuid } from 'uuid';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { useHistory, useLocation, useParams } from 'react-router-dom';

// COMPONENTS
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as ArrowLeftIcon } from '../../assets/svg/chevron-left.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/svg/chevron-right.svg';
import { ReactComponent as ArrowLeft } from '../../assets/svg/arrow-left.svg';

// TYPES
import { RouteParams } from '../../Interfaces';
import {
	SafetyChecklistApproversStaffMember,
	SafetyChecklistEditChecklistItem,
	SafetyChecklistEditCommand
} from '../../api/models';

export interface SafetyChecklists {
	id?: string;
	templateId?: string;
	checklistTemplateId?: string;
	purpose?: string;
	checklistItems: SafetyChecklistEditChecklistItem[];
}

const SafetyChecklistsSubmit: React.FC = () => {
	const history = useHistory();

	// Getting the route id
	const params = useParams<RouteParams>();

	// ALL FORMS DATA
	const initialSafetyChecklistsFormData: SafetyChecklists = {
		id: undefined,
		templateId: undefined,
		checklistTemplateId: undefined,
		purpose: undefined,
		checklistItems: [
			{
				id: uuid(),
				text: undefined
			}
		]
	};

	const [safetyChecklistsFormData, setSafetyChecklistsFormData] = React.useState<SafetyChecklists>(
		initialSafetyChecklistsFormData
	);

	const { state } = useLocation();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Get list of current staff
	const [currentAprroversStaffList, setCurrentApproversStaffList] = React.useState<
		SafetyChecklistApproversStaffMember[]
	>([]);

	const getCurrentStaff = async () => {
		setIsLoading(true);

		const response = await apibridge.getSafetyChecklistApprovers();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setIsLoading(false);

			setCurrentApproversStaffList(result);
		}
	};

	const initialRenderedItemsState = [
		{
			text: undefined
		}
	];

	const [renderedChecklistItems, setRenderedChecklistItems] = React.useState<SafetyChecklistEditChecklistItem[]>([
		...initialRenderedItemsState
	]);

	const getSafetyChecklistsTemplateItemsData = async () => {
		setIsLoading(true);

		// Detect if creating a checklist from a template ( with route id)
		if (params.id) {
			const checklistParams = async () => {
				if (state && state.fromTemplate) {
					if (params.id.startsWith('TemplateId=')) {
						return await apibridge.getSafetyChecklistEdit(``, params.id.split('=')[1]);
					} else if (params.id.startsWith('ChecklistId=')) {
						return await apibridge.getSafetyChecklistEdit('', '', params.id.split('=')[1]);
					}
				} else if (params.id.startsWith('TemplateId=')) {
					return await apibridge.getSafetyChecklistEdit(``, params.id.split('=')[1]);
				} else if (params.id.startsWith('ChecklistId=')) {
					return await apibridge.getSafetyChecklistEdit(params.id.split('=')[1], '');
				} else {
					setSafetyChecklistsFormData((prevState) => ({ ...prevState, checklistTemplateId: params.id }));

					return await apibridge.getSafetyChecklistEdit(params.id);
				}
			};

			const response = await checklistParams();

			if (response) {
				if (response.isError) {
					setFormErrorMessage(
						response.validationErrors.length
							? validationErrorsToString(response.validationErrors)
							: response.message || 'An error has occurred'
					);
				} else if (response.result) {
					const { checklistItems } = response.result;

					setRenderedChecklistItems(checklistItems || []);

					setSafetyChecklistsFormData(response.result as SafetyChecklists);

					if (state && state.fromTemplate) {
						setSafetyChecklistsFormData((prevState) => ({ ...prevState, id: '' }));
					}
				}
			}
		} else {
			// Detect if creating a checklist from blank ( without route id)
			const data = safetyChecklistsFormData;

			const { checklistItems } = data;

			setRenderedChecklistItems(checklistItems);
		}

		setIsLoading(false);
	};

	React.useEffect(() => {
		getSafetyChecklistsTemplateItemsData();
	}, []);

	const formSubmitHandler = async () => {
		setIsLoading(true);

		const formData: SafetyChecklistEditCommand = {
			id: safetyChecklistsFormData.id,
			checklistTemplateId: safetyChecklistsFormData.checklistTemplateId,
			templateId: safetyChecklistsFormData.templateId,
			purpose: safetyChecklistsFormData.purpose,
			checklistItems: [...renderedChecklistItems]
		};

		const response = await apibridge.postSafetyChecklistEdit(formData);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			history.push('/safety-checklists');
		}
	};

	// State to control active step
	const [activeStep, setActiveStep] = React.useState('step1');

	// Logic for Back Button
	const mobileStepHandler = () => {
		if (activeStep === 'step2') {
			setActiveStep('step1');
		}

		if (activeStep === 'step1') {
			history.push('/safety-checklists');
		}
	};

	React.useEffect(() => {
		const pageState = history.location.state;

		if (pageState === undefined || pageState.activeStep === undefined || pageState.activeStep === null) {
			history.replace(window.location.pathname, { activeStep });
		} else if (pageState.activeStep !== activeStep) {
			history.push(window.location.pathname, { activeStep });
		}

		let unlisten = history.listen((location, action) => {
			if (
				action === 'POP' &&
				location.state &&
				location.state.activeStep !== undefined &&
				location.state.activeStep !== null
			) {
				setActiveStep(location.state.activeStep);
			}
		});

		return unlisten;
	}, [activeStep]);

	const goBack = () => {
		mobileStepHandler();
	};

	return (
		<div className="page-general-layout">
			<div className="page-heading">
				<Button className="btn-text-icon" onClick={goBack} buttonIcon={<ArrowLeft className="link-icon" />}>
					Back
				</Button>

				<h1 className="h1"> {params.id ? <>Copy an existing checklist</> : <>Create a new checklist</>} </h1>
			</div>

			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{activeStep === 'step2' ? (
									<p className="sub-grey-text">
										You have added the following items to your checklist. To add more items click on edit details.
										Otherwise create your checklist and start marking items off.
									</p>
								) : null}

								<div className="form-steps-controller-desktop small d-none d-lg-flex">
									<div
										className={`steps-name ${
											activeStep === 'step1' || activeStep === 'step2' || activeStep === 'step3' ? 'active' : ''
										}`}
										onClick={() => setActiveStep('step1')}
									>
										1. Checklist details
									</div>

									<ArrowRightIcon className="step-icon" />

									<div
										className={`steps-name ${activeStep === 'step2' || activeStep === 'step3' ? 'active' : ''}`}
										onClick={() => setActiveStep('step2')}
									>
										2. Preview & Submit
									</div>
								</div>

								<div className="form-steps-controller-mobile d-flex d-lg-none">
									<div className="steps-name-group">
										{activeStep === 'step1' && <div className="steps-name active">1. Checklists details</div>}

										{activeStep === 'step2' && <div className="steps-name active">2. Review & Submit</div>}
									</div>

									<div className="controller-group">
										<div
											className={`controller-wrapper ${activeStep === 'step2' ? 'active' : ''}`}
											onClick={mobileStepHandler}
										>
											<ArrowLeftIcon className="step-controller" />
										</div>

										<div className="controller-wrapper">
											<ArrowRightIcon className="step-controller" />
										</div>
									</div>
								</div>

								<div className="general-divider"></div>

								{activeStep === 'step1' && (
									<StepOne
										safetyChecklistsFormData={safetyChecklistsFormData}
										setSafetyChecklistsFormData={setSafetyChecklistsFormData}
										setActiveStep={setActiveStep}
										renderedChecklistItems={renderedChecklistItems}
										setRenderedChecklistItems={setRenderedChecklistItems}
									/>
								)}

								{activeStep === 'step2' && (
									<StepTwo
										safetyChecklistsFormData={safetyChecklistsFormData}
										setActiveStep={setActiveStep}
										formSubmitHandler={formSubmitHandler}
									/>
								)}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default SafetyChecklistsSubmit;
